import {
  Connect,
  ConnectConfig,
  defaultConnectConfig,
  Session,
} from '@genesislcap/foundation-comms';
import { EventEmitter } from '@genesislcap/foundation-events';
import { App } from '@genesislcap/foundation-shell/app';
import { importPBCAssets } from '@genesislcap/foundation-shell/pbc';
import {configureDesignSystem, FoundationRouterConfiguration} from '@genesislcap/foundation-ui';
import {GENESIS_SOCKET_URL} from '@genesislcap/foundation-utils';
import {
  baseLayerLuminance,
  customElement,
  Container,
  DefaultRouteRecognizer,
  DOM,
  GenesisElement,
  inject,
  observable,
  Registration,
  StandardLuminance
} from '@genesislcap/web-core';
import * as Components from '../components/components';
import { Store, StoreEventDetailMap } from '../../mbm/store';
import designTokens from '../../mbm/styles/design-tokens.json';
import { MainStyles as styles } from './main.styles';
import {
  DynamicTemplate as template,
  LoadingTemplate,
  MainRouterTemplate
} from './main.template';
import { ModuleRegistry } from '@ag-grid-community/core';
import { StatusBarModule } from '@ag-grid-enterprise/status-bar';
import { SideBarModule } from '@ag-grid-enterprise/side-bar';
import { ColumnsToolPanelModule } from '@ag-grid-enterprise/column-tool-panel';
import { FiltersToolPanelModule } from '@ag-grid-enterprise/filter-tool-panel';
import { ClipboardModule } from '@ag-grid-enterprise/clipboard';
import { MenuModule } from '@ag-grid-enterprise/menu';
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model';
import { RangeSelectionModule } from '@ag-grid-enterprise/range-selection';
import { SetFilterModule } from '@ag-grid-enterprise/set-filter';
import {LicenseManager} from '@ag-grid-enterprise/core';
import { KVStorageStatePersistence, StatePersistence } from '@genesislcap/rapid-grid-pro';

// Import is-network-error to make it available globally for foundation-state-machine
import isNetworkError from 'is-network-error';
import {MainCombinedRouterConfig} from "../routes/config-main";
import {PbiStore} from "../../pbi/store";
import { navigateTo } from '@genesislcap/foundation-auth';
import {AppMode, ModeService} from "../services/mode-service";
import {DEFAULT_ROUTE_BY_MODE, ROUTE_LANDING} from "../routes/config-main";
import { ensureLatestAppVersion } from './app-version-check';

// Make isNetworkError available globally
(window as any).isNetworkError = isNetworkError;

ModuleRegistry.registerModules([
  StatusBarModule,
  SideBarModule,
  ColumnsToolPanelModule,
  FiltersToolPanelModule,
  SetFilterModule,
  ClipboardModule,
  MenuModule,
  ServerSideRowModelModule,
  RangeSelectionModule,
]);


const name = 'mbmioi-root';

/**
 * @fires store-connected - Fired when the store is connected.
 * @fires store-ready - Fired when the store is ready.
 */
@customElement({
  name,
  template,
  styles,
})
export class MainApplication extends EventEmitter<StoreEventDetailMap>(GenesisElement) {
  @App app: App;
  @Connect connect!: Connect;
  @Session session!: Session;
  @Container container!: Container;
  @Store store: Store;
  @PbiStore pbiStore: PbiStore;

  @inject(MainCombinedRouterConfig) combinedRouterConfig!: FoundationRouterConfiguration;
  @inject(ModeService) modeService!: ModeService;

  @observable provider!: any;
  @observable ready: boolean = false;
  @observable data: any = null;
  @observable isDarkMode: boolean = true;
  @observable pageToRedirectTo: string = undefined;
  pbcAssetsLoaded: boolean = false;

  async connectedCallback() {
    await ensureLatestAppVersion();

    LicenseManager.setLicenseKey('CompanyName=GENESIS GLOBAL TECHNOLOGY LIMITED,LicensedGroup=Genesis Web,LicenseType=MultipleApplications,LicensedConcurrentDeveloperCount=3,LicensedProductionInstancesCount=4,AssetReference=AG-035099,SupportServicesEnd=8_January_2024_[v2]_MTcwNDY3MjAwMDAwMA==da697ff647e3d57eb719529bdf1506f0');

    // TODO: remove when using cookie-based auth
    this.session.setSessionStorageItem('hostUrl', GENESIS_SOCKET_URL);
    this.registerDIDependencies();
    super.connectedCallback();
    this.setInitialMode();
    this.registerRoutes();
    this.addEventListeners();
    this.readyStore();
    await this.loadPBCs();
    await this.loadRemotes();
    DOM.queueUpdate(() => {
      configureDesignSystem(this.provider, designTokens);
    });
  }

  registerRoutes() {
    this.app.routes.forEach(route => this.app.deregisterRoute(route));
    if (this.ready) {
      this.app.registerRouteCollection(this.combinedRouterConfig.routes);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListeners();
    this.disconnectStore();
  }

  onLuminanceToggle() {
    baseLayerLuminance.setValueFor(
      this.provider,
      baseLayerLuminance.getValueFor(this.provider) === StandardLuminance.DarkMode
        ? StandardLuminance.LightMode
        : StandardLuminance.DarkMode,
    );
  }

  async loadPBCs() {

    if (!this.pbcAssetsLoaded) {
      /**
       * Import PBC assets that may have been added to the ./pbc directory by genx or by hand
       */
      const pbcAssets = await importPBCAssets();
      /**
       * Register bulk assets
       */
      this.app.registerAssets(pbcAssets);
      /**
       * Register the top-level route collection
       */
      this.pbcAssetsLoaded = true;
    }
  }

  async loadRemotes() {
    const { registerComponents } = Components;
    await registerComponents();
    this.ready = true;
  }

  selectTemplate() {
    if (this.ready) {
      return MainRouterTemplate;
    } else {
      return LoadingTemplate;
    }
  }

  private registerDIDependencies() {
    this.container.register(
      Registration.transient(DefaultRouteRecognizer, DefaultRouteRecognizer),
      Registration.instance<ConnectConfig>(ConnectConfig, {
        ...defaultConnectConfig,
        connect: {
          ...defaultConnectConfig.connect,
          heartbeatInterval: 15_000,
        },
      }),
      Registration.singleton(StatePersistence, KVStorageStatePersistence),
    );
  }

  private setInitialMode() {
    const mode = this.modeService.getMode() ?? AppMode.Landing;
    this.pageToRedirectTo = mode === AppMode.Landing ? undefined : mode;
    this.modeService.setMode(mode);
  }

  protected addEventListeners() {
    document.addEventListener('logout', this.onLogout.bind(this));
    this.addEventListener('store-connected', this.store.onConnected);
    this.addEventListener('pbi-store-connected', this.pbiStore.onConnected);
    this.addEventListener('navigateToPage', this.navigateToPage);
  }

  protected async onLogout() {
    this.ready = false;
    this.pageToRedirectTo = undefined;
    this.modeService.setMode(undefined);
    this.disconnectedCallback();
    await this.connectedCallback();
  }

  protected removeEventListeners() {
    this.removeEventListener('store-connected', this.store.onConnected);
    this.removeEventListener('pbi-store-connected', this.pbiStore.onConnected);
    this.removeEventListener('navigateToPage', this.navigateToPage);
  }

  protected navigateToPage(event: CustomEvent<AppMode>) {
    const mode = event.detail;
    this.pageToRedirectTo = mode === AppMode.Landing ? undefined : mode;
    this.modeService.setMode(mode);
    const routeName = this.defaultPathForMode(mode);
    navigateTo('/' + routeName);
  }

  private defaultPathForMode(mode: AppMode | undefined): string {
    return mode ? DEFAULT_ROUTE_BY_MODE[mode] : ROUTE_LANDING;
  }


  protected readyStore() {
    // @ts-ignore
    this.$emit('store-connected', this);
    this.$emit('pbi-store-connected', this);
    this.$emit('store-ready', true);
    this.$emit('pbi-store-ready', true);
  }

  protected disconnectStore() {
    this.$emit('store-disconnected');
  }
}
